import { useRef } from "react";
import RichTextEditor, { type RichTextEditorHandle } from "@/components/common/RichTextEditor";
import {
  FILL_BLANK_SPAN,
  FILL_BLANK_TOKEN,
  promptToHtml,
  promptToPlain,
} from "@/lib/questionScoring";

type PromptFormat = "plain" | "html";

export default function PromptField({
  value,
  format,
  onChange,
  onFormatChange,
  disabled,
  allowBlanks = false,
  placeholder = "Question shown to the learner",
}: {
  value: string;
  format: PromptFormat;
  onChange: (next: string) => void;
  onFormatChange: (next: PromptFormat) => void;
  disabled?: boolean;
  allowBlanks?: boolean;
  placeholder?: string;
}) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const editorRef = useRef<RichTextEditorHandle>(null);

  const setFormat = (next: PromptFormat) => {
    if (next === format) return;
    if (next === "html") onChange(promptToHtml(value));
    else onChange(promptToPlain(value));
    onFormatChange(next);
  };

  const insertBlank = () => {
    if (disabled) return;
    if (format === "html") {
      editorRef.current?.insertHtml(`${FILL_BLANK_SPAN}\u00a0`);
      return;
    }
    const el = textareaRef.current;
    const start = el?.selectionStart ?? value.length;
    const end = el?.selectionEnd ?? start;
    const next = `${value.slice(0, start)}${FILL_BLANK_TOKEN}${value.slice(end)}`;
    onChange(next);
    requestAnimationFrame(() => {
      if (!el) return;
      const caret = start + FILL_BLANK_TOKEN.length;
      el.focus();
      el.setSelectionRange(caret, caret);
    });
  };

  const previewParts = allowBlanks && format === "plain" ? String(value || "").split(/(\$\{blank\})/g) : [];

  return (
    <div className="al-cb-prompt">
      <div className="al-cb-prompt-head">
        <label className="al-cb-label" htmlFor="al-cb-prompt-input">Prompt</label>
        <div className="al-cb-style-tabs" role="tablist" aria-label="Prompt format">
          <button
            type="button"
            role="tab"
            aria-selected={format === "plain"}
            className={`al-cb-style-tab${format === "plain" ? " al-cb-style-tab--on" : ""}`}
            disabled={disabled}
            onClick={() => setFormat("plain")}
          >
            Plain
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={format === "html"}
            className={`al-cb-style-tab${format === "html" ? " al-cb-style-tab--on" : ""}`}
            disabled={disabled}
            onClick={() => setFormat("html")}
          >
            Rich text
          </button>
        </div>
      </div>

      {allowBlanks ? (
        <div className="al-cb-blank-bar">
          <button
            type="button"
            className="al-cb-btn al-cb-btn--ghost"
            disabled={disabled}
            onMouseDown={(e) => e.preventDefault()}
            onClick={insertBlank}
          >
            Insert blank
          </button>
          <span className="al-cb-style-hint">Puts a blank at the cursor.</span>
        </div>
      ) : null}

      {format === "plain" ? (
        <>
          <textarea
            ref={textareaRef}
            id="al-cb-prompt-input"
            className="al-cb-textarea"
            rows={2}
            value={value}
            disabled={disabled}
            placeholder={allowBlanks ? "My name is  and it is cool! — then Insert blank where they type." : placeholder}
            onChange={(e) => onChange(e.target.value)}
          />
          {allowBlanks && previewParts.some((part) => part === FILL_BLANK_TOKEN) ? (
            <p className="al-cb-blank-preview" aria-label="Prompt with blanks">
              {previewParts.map((part, index) => {
                if (part !== FILL_BLANK_TOKEN) return <span key={index}>{part}</span>;
                const n = previewParts.slice(0, index).filter((token) => token === FILL_BLANK_TOKEN).length + 1;
                return (
                  <span key={index} className="al-cb-blank">
                    {n}
                  </span>
                );
              })}
            </p>
          ) : allowBlanks ? (
            <p className="al-cb-style-hint">Insert a blank where the learner should type.</p>
          ) : null}
        </>
      ) : (
        <RichTextEditor
          ref={editorRef}
          value={value}
          onChange={onChange}
          disabled={disabled}
          placeholder={placeholder}
          minHeight="3.5rem"
          allowBlanks={allowBlanks}
        />
      )}
    </div>
  );
}
